import { formatUploadJobFailure, resolveUploadErrorMessage } from "@/upload";
import type { DatasetUploadFailure, DatasetUploadImportProgress } from "@/api";

// 업로드 표면(테이블·다이얼로그·위젯)의 표시 포맷터. 에러 코드→문구의 source of truth 는
// 업로드 모듈(@/upload uploadErrors)이 소유한다. 이 파일은 표시 전용 헬퍼만 추가한다.

// 업로드 흐름의 에러를 사용자 메시지로 변환한다(코드 매핑→서버 메시지→Error 메시지 폴백).
export function resolveErrorMessage(error: unknown): string {
  return resolveUploadErrorMessage(error);
}

// status=failed 잡의 실패 단계·코드를 사용자 메시지로 변환한다.
export function formatUploadFailure(
  failure: DatasetUploadFailure | null,
  uploadVerified: boolean,
): string {
  return formatUploadJobFailure(failure, uploadVerified);
}

// 반입이 확정한 에피소드 구간. **null 은 "0 에피소드"가 아니다.** manifest 가 아직 확정되지 않아
// 규모 자체를 모르는 상태다. 그래서 건수를 표시하지 않는다. 0/0 으로 표시하면 "아무것도 처리하지
// 못했다"로 보인다. 에피소드가 0건인 데이터셋도 같은 이유로 비운다. 서버는 그때 진행률 100 을 보낸다.
export function formatCommittedEpisodes(
  progress: DatasetUploadImportProgress | null,
): string | null {
  if (progress === null || progress.episode_count === 0) {
    return null;
  }
  const done = progress.commit_cursor.toLocaleString();
  const total = progress.episode_count.toLocaleString();
  return `${done}/${total} 에피소드`;
}

const UPLOAD_BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

// 업로드 진행 표기 전용 byte 포맷. 좁은 위젯에 맞춰 10 이상은 정수, 미만은 소수 1자리로 줄인다.
// 0 이하는 "0 B"로 표시한다(시작 시점 표시). 목록·상세의 일반 크기 표기는 `@/utils` formatBytes 가
// 따로 소유한다(100 기준·빈칸 "-").
export function formatUploadBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return "0 B";
  }
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < UPLOAD_BYTE_UNITS.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const digits = size >= 10 || unitIndex === 0 ? 0 : 1;
  return `${size.toFixed(digits)} ${UPLOAD_BYTE_UNITS[unitIndex]}`;
}

// 업로드 경로의 실측 총 스루풋. ETA 는 이 단일 값으로만 계산한다. 잡을 여러 개 걸어도 총량은 늘지
// 않는다. 엔진의 전송 슬롯은 하나다. 한 잡 안의 파일 동시 전송 수는 처리량을 정한다(`UploadEngine`
// 의 `fileConcurrency`).
export const UPLOAD_THROUGHPUT_BYTES_PER_SEC = 15 * 1000 * 1000;

// 총 바이트 → 예상 소요 문구("약 45초"/"약 14분"/"약 2시간 10분"). 0 이하는 "약 1초 미만".
export function formatUploadEta(totalBytes: number): string {
  if (!Number.isFinite(totalBytes) || totalBytes <= 0) {
    return "약 1초 미만";
  }
  const seconds = Math.ceil(totalBytes / UPLOAD_THROUGHPUT_BYTES_PER_SEC);
  if (seconds < 60) {
    return `약 ${seconds}초`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `약 ${minutes}분`;
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes === 0 ? `약 ${hours}시간` : `약 ${hours}시간 ${restMinutes}분`;
}

// 알릴 채널 누락 에피소드 수다. 1 이상일 때만 수를 돌려준다. 0(없음)과 null(서버가 세기 전이거나
// 옛 잡이라 모름)은 둘 다 알릴 것이 없으므로 null 이다. 업로드 창의 완료 문구와 업로드 현황 표가
// 이 판정을 같이 쓴다. 예) 50 → 50, 0 → null, null → null
export function missingChannelNoticeCount(count: number | null | undefined): number | null {
  return count === null || count === undefined || count === 0 ? null : count;
}
